import { useEffect, useRef } from 'react';
import { WS_BASE } from '@/lib/config';
import { authedWebSocketProtocols } from '@/lib/ws';
import { useBrowserStore } from '@/stores/browserStore';

const RECONNECT_DELAY_MS = 2000;

/** Opens a lightweight WebSocket to /browser/harness-stream and keeps
 *  browserStore.harnessConnected in sync with whether the sidecar Chrome
 *  CDP is reachable. Mounted once at the app shell level. */
export function useHarnessStatus(): void {
  const setHarnessConnected = useBrowserStore((s) => s.setHarnessConnected);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const closedRef = useRef(false);

  useEffect(() => {
    closedRef.current = false;

    const connect = () => {
      if (closedRef.current) return;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = undefined;
      }

      const wsUrl = `${WS_BASE}/browser/harness-stream`;

      try {
        const ws = new WebSocket(wsUrl, authedWebSocketProtocols());
        wsRef.current = ws;

        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data) as { type: string; connected?: boolean };
            if ((msg.type === 'init' || msg.type === 'status') && typeof msg.connected === 'boolean') {
              setHarnessConnected(msg.connected);
            }
          } catch {
            // Ignore malformed messages
          }
        };

        ws.onclose = () => {
          setHarnessConnected(false);
          wsRef.current = null;
          if (!closedRef.current) {
            reconnectTimeoutRef.current = setTimeout(connect, RECONNECT_DELAY_MS);
          }
        };

        ws.onerror = () => {
          // Closure follows; handled by onclose
        };
      } catch {
        setHarnessConnected(false);
        reconnectTimeoutRef.current = setTimeout(connect, RECONNECT_DELAY_MS);
      }
    };

    connect();

    return () => {
      closedRef.current = true;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [setHarnessConnected]);
}
